import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import {
  mdiArchiveOutline,
  mdiArrowRightBold,
  mdiCalendarClock,
  mdiCheckBold,
  mdiFilePdfBox,
  mdiLockCheck,
  mdiRestore,
} from "@mdi/js"
import CreateAppointmentOverlay from "../../../../../_shared/_modules/create-appointment-overlay"
import { buildFixedWorkOrderAppointmentInit } from "../../../../../_shared/_logic/init"
import ScheduleEventReadOverlay from "../../../../../_shared/_modules/schedule-event-read-overlay"
import {
  WORK_ORDER_ACTION_TOOLTIP_CLASS,
  workOrderActionTooltips,
} from "../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
import { schedulingActionTooltips } from "../../../../../../../../../_shared/_logic/action-tooltips"
import type { WorkOrderRow } from "../../../../../_shared/_logic/work-order-table-model"
import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"
import {
  completeWorkOrderRow,
  archiveWorkOrderRow,
  downloadWorkOrderCommunicationProofRow,
  reopenWorkOrderRow,
  type TableActionRunner,
} from "../row-action-commands"
import type { TableRowAction } from "@kontextwerk/web-sdk/core/layout/table"
type EmployeeOption = { id: string; label: string }
type Props = {
  row: WorkOrderRow
  organizationId: string
  workOrder: WorkOrder
  scopedCanEdit: boolean
  canPlanAppointments: boolean
  loading: boolean
  actionLoading: boolean
  relevantScheduleEvent?: ScheduleEvent | null
  scheduleEvents?: ScheduleEvent[]
  effectiveProperties?: Property[]
  effectiveCustomers?: Customer[]
  effectiveContactPoints?: ContactPoint[]
  contactOrders?: ContactOrder[]
  employees?: EmployeeOption[]
  planningSettings?: PlanningSettings | null
  workingHoursByUser?: WorkingHoursByUser
  allEmployeeIds?: string[]
  runTableAction: TableActionRunner
  onChanged?: () => Promise<void> | void
  onCreated?: () => Promise<void> | void
  onConfirmAppointment?: (event: ScheduleEvent, workOrder: WorkOrder) => void
  onRescheduleAppointment?: (event: ScheduleEvent, workOrder: WorkOrder) => void
  onScheduleEventUpdated?: (
    event?: ScheduleEvent | null,
    removedId?: string | null,
  ) => Promise<void> | void
}
export default function buildRowActions({
  row,
  organizationId,
  workOrder,
  scopedCanEdit,
  canPlanAppointments,
  loading,
  actionLoading,
  relevantScheduleEvent = null,
  scheduleEvents = [],
  effectiveProperties = [],
  effectiveCustomers = [],
  effectiveContactPoints = [],
  contactOrders = [],
  employees = [],
  planningSettings = null,
  workingHoursByUser = {},
  allEmployeeIds = [],
  runTableAction,
  onChanged = async () => {},
  onCreated = async () => {},
  onConfirmAppointment = () => {},
  onRescheduleAppointment = () => {},
  onScheduleEventUpdated = async () => {},
}: Props): TableRowAction[] {
  const isOpen = workOrder.status === "OPEN"
  const isTerminal = workOrder.status === "COMPLETED" || workOrder.status === "CANCELED"
  const isArchived = Boolean(workOrder.archived_at)
  const openAppointmentOverlay = () => {
    if (!organizationId || loading || actionLoading || !canPlanAppointments) return

    const hasEvent = Boolean(relevantScheduleEvent?.id)
    openOverlay({
      title: hasEvent ? "Termin" : "Termin anlegen",
      ...(hasEvent ? { layout: "record" as const } : {}),
      content: hasEvent ? ScheduleEventReadOverlay : CreateAppointmentOverlay,
      bodyPadding: "none",
      closeOnParentOpen: true,
      isDark: true,
      properties: hasEvent
        ? {
            organizationId,
            event: relevantScheduleEvent,
            employees,
            workOrders: [workOrder],
            properties: effectiveProperties,
            customers: effectiveCustomers,
            contactPoints: effectiveContactPoints,
            contactOrders,
            scheduleEvents,
            workingHoursByUser,
            forceReadOnly: !canPlanAppointments,
            onSaved: async (updated: ScheduleEvent) => {
              await onScheduleEventUpdated(updated, null)
            },
            onDeleted: async (id: string) => {
              await onScheduleEventUpdated(null, id)
            },
            onRefresh: onCreated,
          }
        : {
            init: buildFixedWorkOrderAppointmentInit({
              organizationId,
              employees,
              workOrder,
              properties: effectiveProperties,
              customers: effectiveCustomers,
              contactPoints: effectiveContactPoints,
              scheduleEvents,
              workingHoursByUser,
              defaultStart: null,
              defaultEnd: null,
              defaultFlexType: "HARD",
              defaultStatus: "RESERVED",
              flexSlotMinutes: planningSettings?.flex_slot_minutes ?? null,
              flexSlotsPerDay: planningSettings?.flex_slots_per_day ?? null,
              allEmployeeIds,
            }),
            onCreated: async (created: ScheduleEvent) => {
              await onScheduleEventUpdated(created, null)
              await onCreated()
            },
          },
    })
  }
  const handleArchive = async () => {
    await archiveWorkOrderRow({ row, actionLoading, runTableAction, onChanged })
  }
  const handleDownloadCommunicationProof = async () => {
    await downloadWorkOrderCommunicationProofRow({ row, actionLoading, runTableAction })
  }
  const handleComplete = async () => {
    await completeWorkOrderRow({
      row,
      scopedCanEdit,
      actionLoading,
      runTableAction,
      onChanged,
    })
  }
  const handleReopen = () => {
    reopenWorkOrderRow({
      organizationId,
      row,
      properties: effectiveProperties,
      customers: effectiveCustomers,
      contactPoints: effectiveContactPoints,
      employees,
      scheduleEvents,
      planningSettings,
      workingHoursByUser,
      allEmployeeIds,
      scopedCanEdit,
      actionLoading,
      runTableAction,
      onChanged,
    })
  }
  const actions: TableRowAction[] = []

  if (canPlanAppointments && isOpen && !isArchived) {
    actions.push({
      id: "action-0",
      label: "Termin und Ankündigung bearbeiten",
      onClick: () => openAppointmentOverlay(),
      icon: mdiCalendarClock,
      disabled: loading || actionLoading || !canPlanAppointments,
      tooltip: workOrderActionTooltips.rowContactPlanning,
    })
  }

  const hasEvent = Boolean(relevantScheduleEvent?.id)
  const canConfirmAppointment =
    hasEvent &&
    ["RESERVED", "ANNOUNCED"].includes(relevantScheduleEvent?.status ?? "") &&
    canPlanAppointments &&
    !isArchived
  const canRescheduleAppointment =
    hasEvent &&
    ["RESERVED", "ANNOUNCED", "CONFIRMED", "COMPLETED"].includes(
      relevantScheduleEvent?.status ?? "",
    ) &&
    canPlanAppointments &&
    !isArchived

  if (canConfirmAppointment && relevantScheduleEvent) {
    actions.push({
      id: "action-confirm-appointment",
      label: "Termin bestätigen",
      onClick: () => onConfirmAppointment(relevantScheduleEvent, workOrder),
      icon: mdiLockCheck,
      disabled: loading || actionLoading || !canPlanAppointments,
      tooltip: schedulingActionTooltips.eventConfirm,
      display: "menu",
    })
  }

  if (canRescheduleAppointment && relevantScheduleEvent) {
    actions.push({
      id: "action-reschedule-appointment",
      label: "Termin verschieben",
      onClick: () => onRescheduleAppointment(relevantScheduleEvent, workOrder),
      icon: mdiArrowRightBold,
      disabled: loading || actionLoading || !canPlanAppointments,
      tooltip: schedulingActionTooltips.eventReschedule,
      display: "menu",
    })
  }

  if (scopedCanEdit && isOpen && !isArchived) {
    actions.push({
      id: "action-2",
      label: "Auftrag als erledigt markieren",
      onClick: () => handleComplete(),
      icon: mdiCheckBold,
      disabled: loading || actionLoading || !scopedCanEdit,
      tooltip: workOrderActionTooltips.rowComplete,
      display: "menu",
    })
  }

  if (scopedCanEdit && isTerminal && !isArchived) {
    actions.push({
      id: "action-3",
      label: "Auftrag wieder öffnen",
      onClick: () => handleReopen(),
      icon: mdiRestore,
      disabled: loading || actionLoading || !scopedCanEdit,
      tooltip: workOrderActionTooltips.rowReopen,
      display: "inline",
    })
  }

  if (scopedCanEdit && !isArchived) {
    actions.push({
      id: "action-communication-proof",
      label: "Kommunikationsnachweis (PDF)",
      onClick: () => handleDownloadCommunicationProof(),
      icon: mdiFilePdfBox,
      disabled: loading || actionLoading || !scopedCanEdit,
      display: "menu",
    })
    actions.push({
      id: "action-4",
      label: "Auftrag archivieren",
      onClick: () => handleArchive(),
      icon: mdiArchiveOutline,
      disabled: loading || actionLoading || !scopedCanEdit,
      tooltip: workOrderActionTooltips.rowArchive,
    })
  }

  return actions
}
